// bookie-wine-preview — bk13 Olov Yurak.
//
// Paper-invitation preview. A deep wine-foil border wraps a blush-cream
// inner card. Centerpiece is a roundel holding two interlinked wedding
// rings with a tiny gold stone at the apex — the engagement motif. The
// composition stays disciplined: foil > paper > eyebrow > medallion >
// names > footer. All colors come from LayoutCtx so the same component
// renders for the catalog tile and the rendered card.
import { type LayoutCtx } from "./_shared";

const FOIL =
  "linear-gradient(135deg, #6E1A22 0%, #8E2E36 22%, #B25762 45%, #8E2E36 65%, #6E1A22 100%)";

export function BookieWinePreviewLayout({ names, date, venue, accent, ink, bg, font }: LayoutCtx) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span style={{ width: 14, height: 1, background: accent }} />
          TAKLIFNOMA
          <span style={{ width: 14, height: 1, background: accent }} />
        </div>

        {/* Rings-roundel medallion */}
        <div
          style={{
            width: "62%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: FOIL,
              padding: "5%",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: bg,
                padding: "6%",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg viewBox="0 0 100 100" style={{ width: "85%", height: "85%" }} aria-hidden>
                {/* Two interlinked rings */}
                <ellipse cx="38" cy="58" rx="20" ry="20" stroke={accent} strokeWidth="1.6" fill="none" />
                <ellipse cx="38" cy="58" rx="16" ry="16" stroke={accent} strokeWidth="0.5" fill="none" opacity="0.4" />
                <ellipse cx="62" cy="58" rx="20" ry="20" stroke={accent} strokeWidth="1.6" fill="none" />
                <ellipse cx="62" cy="58" rx="16" ry="16" stroke={accent} strokeWidth="0.5" fill="none" opacity="0.4" />
                {/* Gold stone at top */}
                <circle cx="50" cy="32" r="3.2" fill="#C9A24A" />
                <circle cx="50" cy="32" r="1.3" fill={bg} opacity="0.85" />
                <path
                  d="M50 24 L50 20 M50 40 L50 44 M42 32 L38 32 M58 32 L62 32"
                  stroke="#C9A24A"
                  strokeWidth="0.5"
                  strokeLinecap="round"
                  opacity="0.6"
                />
                {/* Subtle base ribbon */}
                <path
                  d="M28 86 Q 50 82 72 86"
                  stroke={accent}
                  strokeWidth="0.6"
                  fill="none"
                  opacity="0.7"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Names */}
        <div
          style={{
            fontFamily: font,
            fontStyle: "italic",
            fontSize: 28,
            lineHeight: 1.05,
            fontWeight: 500,
            color: ink,
            letterSpacing: "0.01em",
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        {/* Footer */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TAKLIFNOMA · {date}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
